import { toLocalDateString } from './localDate';

/**
 * Calendar dates ("date-only" values) without a time-zone shift.
 *
 * The trap these helpers exist for: `new Date('2026-10-09')` is UTC
 * midnight by spec, so in any US zone it reads back as the evening of
 * 10/8 and `toLocaleDateString()` prints the day before. Report footers,
 * the analysis grids and their exports all showed a day early that way,
 * and the test suite (pinned to TZ=UTC) could not see it.
 *
 * The reverse trap is on the wire: `JSON.stringify(new Date())` writes a
 * UTC instant ("...T01:30:00.000Z"). The report generators read settings
 * with Newtonsoft and `DateTimeZoneHandling.Unspecified`
 * (DietechShareUtilsVBCore_Wrapper/modGlobals.vb:99-105), which keeps the
 * UTC wall clock — so after ~8pm Eastern a report ran for tomorrow. An
 * unzoned "yyyy-MM-ddTHH:mm:ss" (or a bare "yyyy-MM-dd") is read as that
 * exact calendar day, which is what WinForms sends (`Now`/`Today`).
 *
 * Everything here is browser-local: these are days the user picked or
 * days the API returned, and re-interpreting them in another zone is what
 * moves them. For "today in the facility's zone" see `useFacilityDate`.
 */

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?$/;
const HAS_ZONE_RE = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/**
 * The local calendar day `value` names, as a local-midnight Date.
 *
 * - `Date`: its local day (time dropped).
 * - `'yyyy-MM-dd'` / `'yyyy-MM-ddTHH:mm:ss[.fff]'` with no zone: that day,
 *   read as written (never as UTC).
 * - A string with a zone (`Z`, `-04:00`): a real instant; its local day.
 * - Anything else `Date` can parse (e.g. `'10/9/2026'`): its local day.
 *
 * Returns null for empty / unparseable input.
 */
export function parseDateOnly(value: unknown): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  if (typeof value !== 'string') return null;
  const s = value.trim();
  const m = DATE_ONLY_RE.exec(s);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (!HAS_ZONE_RE.test(s) && !/[a-z]/i.test(s) && !s.includes('/')) return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** `'yyyy-MM-dd'` for the local day `value` names; `''` when it has none. */
export function toDateOnlyString(value: unknown): string {
  const d = parseDateOnly(value);
  return d ? toLocalDateString(d) : '';
}

/** Today's local calendar day as `'yyyy-MM-dd'` (evaluated per call). */
export function todayDateOnly(): string {
  return toLocalDateString(new Date());
}

/**
 * `value` moved by whole calendar days, as `'yyyy-MM-dd'`. Uses local
 * date arithmetic, so a DST change in between never lands on the wrong day.
 */
export function addDaysDateOnly(value: unknown, days: number): string {
  const d = parseDateOnly(value);
  if (!d) return '';
  d.setDate(d.getDate() + days);
  return toLocalDateString(d);
}

/**
 * `M/D/YYYY` (what `toLocaleDateString('en-US')` and .NET's en-US
 * `ToShortDateString` print) for the day `value` names; `fallback` when it
 * has none. No locale/ICU involved, so it is the same on every machine.
 */
export function formatDateOnly(value: unknown, fallback = ''): string {
  const d = parseDateOnly(value);
  if (!d) return fallback;
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}

/**
 * `'M/D/YYYY - M/D/YYYY'`. With `collapseSameDay` a one-day range prints
 * one date, the way the server's `NumberOfDays = 1` / `StartDate.Date =
 * EndDate.Date` branches do.
 */
export function formatDateOnlyRange(
  start: unknown,
  end: unknown,
  options: { collapseSameDay?: boolean } = {},
): string {
  const s = formatDateOnly(start);
  const e = formatDateOnly(end);
  if (options.collapseSameDay && s && s === e) return s;
  return `${s} - ${e}`;
}

/**
 * Footer text for "N days starting at `start`" — the shape of the server's
 * `DateFor` on the Production / Posting Menu / Employee settings classes:
 * one day prints one date, otherwise `start - start+(N-1)`.
 */
export function formatDayCountRange(start: unknown, numberOfDays: unknown): string {
  if (!parseDateOnly(start)) return '';
  const n = Math.max(1, Math.trunc(Number(numberOfDays)) || 1);
  if (n === 1) return formatDateOnly(start);
  return formatDateOnlyRange(start, addDaysDateOnly(start, n - 1));
}

/** Sort comparator on the calendar day (unparseable values sort last). */
export function compareDateOnly(a: unknown, b: unknown): number {
  const da = parseDateOnly(a);
  const db = parseDateOnly(b);
  if (!da && !db) return 0;
  if (!da) return 1;
  if (!db) return -1;
  return da.getTime() - db.getTime();
}

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * `'yyyy-MM-ddTHH:mm:ss'` in local time with NO zone — how a `Date` has to
 * travel to the report generators (see the module note) so Newtonsoft keeps
 * the user's wall clock instead of UTC's.
 */
export function toLocalWireDateTime(date: Date): string {
  return `${toLocalDateString(date)}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

/** `'yyyy-MM-ddT00:00:00'` for the local day `value` names; `''` if none. */
export function toWireDate(value: unknown): string {
  const s = toDateOnlyString(value);
  return s ? `${s}T00:00:00` : '';
}

/**
 * `JSON.stringify` that writes every `Date` as local, unzoned
 * `'yyyy-MM-ddTHH:mm:ss'` instead of a UTC instant. Use it for anything a
 * VB report generator deserializes (report settings JSON).
 */
export function stringifyWithLocalDates(value: unknown): string {
  return JSON.stringify(value, function replacer(this: Record<string, unknown>, key: string, v: unknown) {
    // `v` has already been through Date#toJSON; the holder still has the Date.
    const raw = this[key];
    if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : toLocalWireDateTime(raw);
    return v;
  });
}

/**
 * Copy of `values` with every `Date` under `dateKeys` replaced by its
 * `'yyyy-MM-dd'` — the shape the report form's date picker emits. Function
 * defaults that return `new Date()` (now, with a time of day) then reach
 * `buildSettings` exactly like a day the user picked.
 */
export function dateValuesAsDateOnly(
  values: Record<string, unknown>,
  dateKeys: Iterable<string>,
): Record<string, unknown> {
  let out: Record<string, unknown> | null = null;
  for (const key of dateKeys) {
    const v = values[key];
    if (!(v instanceof Date)) continue;
    out ??= { ...values };
    out[key] = toDateOnlyString(v);
  }
  return out ?? values;
}
